import ZTable, { IFilterItem, IColumnType, ITableRef } from '@/components/ZTable';
import { formatDisplayDate, formatDisplayFullDate } from '@/config';
import { TicketStatusType } from '@/constants';
import MainContent from '@/layouts/MainContent';
import React from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip, Button, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { sumBy } from 'lodash';
import { TicketPagingDto, TicketConflictListingDto } from '@/__generated';
import { getFullName } from '@/helpers/string';

const PAGE_TITLE = 'ticketConflictPage_Title';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'bookingRefNo', label: 'ticketBooking_BookingRefNo' },
  { type: 'TEXT', name: 'ticketRefNo', label: 'ticketBooking_TicketRefNo' },
  { type: 'TEXT', name: 'tripCode', label: 'ticketConflictPage_TripCode' },

  { type: 'TEXT', name: 'PassengerName', label: 'bookingPage_PassengerName' },

  {
    type: 'LIST',
    name: 'ticketStatus',
    label: 'base.Status',
    listData: TicketStatusType.__getSelectOptions(),
  },
];

const columns: IColumnType<TicketPagingDto>[] = [
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_TicketRefNo',
    dataIndex: 'ticketRefNo',
    sorter: true,
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_FromStation',
    dataIndex: 'sourceStation',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'ticketBooking_ToStation',
    dataIndex: 'destinationStation',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'tripsPage_TripName',
    dataIndex: 'tripName',
    ellipsis: true,
    width: 140,
  },
  {
    title: 'base.Status',
    dataIndex: 'ticketStatus',
    sorter: true,
    ellipsis: true,
    render: v => <Tag color={TicketStatusType.__getBgColor(v)}>{TicketStatusType.__getValue(v)}</Tag>,
    width: 120,
  },
  {
    title: 'ticketBooking_Passenger',
    dataIndex: 'passengerFirstName',
    ellipsis: true,
    width: 150,
    render: (_, record) => getFullName(record.passengerFirstName, record.passengerLastName),
  },
  {
    title: 'ticketBooking_SeatNo',
    dataIndex: 'seatName',
    ellipsis: true,
    width: 100,
    render: (_, row) => [row.seatName, row.ticketClassName?.at(0)].join(' - '),
  },
  {
    title: 'bookingPage_DepartureDate',
    dataIndex: 'departureDateTime',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },

  {
    title: 'ticketBooking_BookingDate',
    dataIndex: 'createdOn',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
  {
    title: 'ticketConflictPage_ConflictReason',
    dataIndex: 'conflictReason',
    ellipsis: true,
    align: 'center',
    fixed: 'right',
    render: v => (
      <Tooltip title={<Trans>{v}</Trans>}>
        <Button danger type="link" icon={<InfoCircleOutlined />} />
      </Tooltip>
    ),

    width: 120,
  },
];

const width = sumBy(columns, x => x.width || 0);

export default function TicketConflictPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<TicketConflictListingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/bookings/ticket-conflict-management/get-conflict-tickets"
          bordered
          scroll={{ x: width }}
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          searchBtnAlignLeft
          rowClassName={row => (row.onlineBookingId ? 'highlight-row' : '')}
          actionColumnWidth={130}
        />
      </div>
    </MainContent>
  );
}
